import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import { api, API, errText } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Search, Loader2, CheckCircle2, XCircle, FileText, Send, AlertTriangle,
  Store, User, Calendar, Tag, ShieldAlert, Eye, ExternalLink, Download,
} from "lucide-react";

const money = (v, sym = "$") =>
  `${sym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fdate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-");

export default function Workspace() {
  const [search, setSearch] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listMsg, setListMsg] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);

  const loadQuotes = async (term) => {
    setListLoading(true);
    setListMsg("");
    try {
      const r = await api.get("/quotes", { params: term ? { search: term } : {} });
      setQuotes(r.data.quotes || []);
      if (r.data.message) setListMsg(r.data.message);
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Could not load quotes");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadQuotes("");
  }, []);

  const openQuote = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setPreviewUrl(null);
    try {
      const r = await api.get(`/quotes/${id}`);
      setDetail(r.data);
      setRecipient(r.data.customer?.email || "");
    } catch (e) {
      toast.error(errText(e.response?.data?.detail) || "Could not load quote");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const loadPreview = async () => {
    if (!detail) return;
    setPreviewOpen(true);
    if (previewUrl) return;
    try {
      const r = await api.get(`/quotes/${detail.id}/pdf`, { responseType: "blob" });
      setPreviewUrl(URL.createObjectURL(r.data));
    } catch (e) {
      toast.error("Could not generate PDF preview");
    }
  };

  const downloadPdf = async () => {
    if (!detail) return;
    try {
      const r = await api.get(`/quotes/${detail.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quote-${detail.reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error("Could not download PDF");
    }
  };

  const doSend = async () => {
    if (!detail) return;
    setSending(true);
    try {
      await api.post(`/quotes/${detail.id}/send`, { recipient_email: recipient });
      toast.success(`Quote #${detail.reference} sent to ${recipient}`);
      setSendOpen(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error("Send blocked — totals do not match Lightspeed.");
      } else {
        toast.error(errText(d) || "Could not send quote");
      }
    } finally {
      setSending(false);
    }
  };

  const valid = detail?.validation?.passed;

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="mx-auto grid max-w-[1400px] gap-6 px-6 py-6 lg:grid-cols-[minmax(360px,1fr)_1.4fr]">
        {/* LEFT: search + list */}
        <section data-testid="quote-list-panel">
          <div className="mb-4">
            <h1 className="font-head text-2xl font-600 tracking-tight text-slate-900">
              Completed quotes
            </h1>
            <p className="text-sm text-slate-500">Search a quote, validate its totals, then send by SKU.</p>
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); loadQuotes(search); }}
            className="mb-4 flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by quote reference…"
                data-testid="quote-search-input"
                className="pl-9 focus-visible:ring-2 focus-visible:ring-blue-500"
              />
            </div>
            <Button type="submit" data-testid="quote-search-btn" className="bg-slate-900 hover:bg-slate-800">
              Search
            </Button>
          </form>

          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            {listLoading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : listMsg ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="quote-list-message">
                {listMsg}
              </div>
            ) : quotes.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No quotes found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Ref</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((q) => (
                    <TableRow
                      key={q.id}
                      onClick={() => openQuote(q.id)}
                      data-testid={`quote-row-${q.reference}`}
                      className={`cursor-pointer transition-colors ${
                        selectedId === q.id ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <TableCell className="font-mono font-500 text-slate-900">#{q.reference}</TableCell>
                      <TableCell className="text-slate-600">{q.outlet_name}</TableCell>
                      <TableCell className="text-slate-500">{fdate(q.created_at)}</TableCell>
                      <TableCell className="text-right font-mono text-slate-900">
                        {money(q.total_incl)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </section>

        {/* RIGHT: detail */}
        <section data-testid="quote-detail-panel">
          {!selectedId ? (
            <EmptyDetail />
          ) : detailLoading ? (
            <div className="flex h-64 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {!detail.sku_scope_available && (
                <div
                  className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-800"
                  data-testid="sku-scope-warning"
                >
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <b>SKUs unavailable.</b> The Lightspeed token lacks product-read permission, so
                    lines show <b>NO-SKU</b>. Grant Products scope to the token to display real SKUs.
                  </div>
                </div>
              )}

              {/* header card */}
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-head text-2xl font-600 tracking-tight text-slate-900">
                        Quote #{detail.reference}
                      </h2>
                      <Badge variant="outline" className="border-slate-200 text-slate-500">
                        {detail.status}
                      </Badge>
                    </div>
                    <div className="mt-3 grid gap-1.5 text-sm text-slate-600">
                      <span className="flex items-center gap-2"><Store className="h-3.5 w-3.5 text-slate-400" />{detail.outlet?.name}</span>
                      <span className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-slate-400" />{detail.customer?.name}{detail.customer?.email ? ` · ${detail.customer.email}` : ""}</span>
                      <span className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-slate-400" />{fdate(detail.created_at)} · Salesperson: {detail.salesperson || "—"}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={loadPreview} data-testid="preview-btn" className="gap-1.5">
                      <Eye className="h-4 w-4" /> Preview PDF
                    </Button>
                    <Button variant="outline" onClick={downloadPdf} data-testid="download-btn" className="gap-1.5">
                      <Download className="h-4 w-4" /> Download
                    </Button>
                    <Button
                      onClick={() => setSendOpen(true)}
                      disabled={!valid}
                      data-testid="send-btn"
                      className={valid ? "gap-1.5 bg-blue-600 hover:bg-blue-700" : "gap-1.5"}
                    >
                      <Send className="h-4 w-4" /> Send
                    </Button>
                  </div>
                </div>
              </div>

              {/* validation panel */}
              <ValidationPanel v={detail.validation} sym={detail.currency_symbol} />

              {/* line items */}
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-3">
                  <h3 className="flex items-center gap-2 font-head text-sm font-600 text-slate-900">
                    <Tag className="h-4 w-4 text-slate-400" /> Line items (SKU only)
                  </h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit</TableHead>
                      <TableHead className="text-right">Discount</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Line total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.lines.map((ln, i) => (
                      <TableRow key={i} data-testid={`line-row-${i}`}>
                        <TableCell>
                          <span className={`font-mono font-500 ${ln.sku_missing ? "text-amber-600" : "text-slate-900"}`}>
                            {ln.sku}
                          </span>
                          {ln.note && <div className="text-xs text-slate-400">{ln.note}</div>}
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-600">{ln.quantity}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600">{money(ln.unit_price, detail.currency_symbol)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600">{money(ln.discount, detail.currency_symbol)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-600">{money(ln.line_tax, detail.currency_symbol)}</TableCell>
                        <TableCell className="text-right font-mono font-500 text-slate-900">{money(ln.line_total, detail.currency_symbol)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="w-64 space-y-1.5 text-sm">
                    <Row label="Subtotal (excl. tax)" value={money(detail.totals.subtotal, detail.currency_symbol)} />
                    <Row label="Tax" value={money(detail.totals.tax, detail.currency_symbol)} />
                    <div className="my-1 border-t border-slate-200" />
                    <Row label="Total" value={money(detail.totals.total, detail.currency_symbol)} bold />
                    {detail.totals.savings > 0 && (
                      <Row label="You save" value={money(detail.totals.savings, detail.currency_symbol)} green />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </section>
      </main>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-head">PDF preview — Quote #{detail?.reference}</DialogTitle>
            <DialogDescription>SKU-only reconstruction of the Lightspeed quote.</DialogDescription>
          </DialogHeader>
          {previewUrl ? (
            <>
              <object
                data={previewUrl}
                type="application/pdf"
                className="h-[70vh] w-full rounded-md border border-slate-200"
                aria-label="PDF preview"
                data-testid="pdf-preview-frame"
              >
                <div className="flex h-64 flex-col items-center justify-center gap-3 text-slate-500">
                  <p className="text-sm">Your browser blocked the inline preview.</p>
                  <a href={previewUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                    Open the PDF in a new tab
                  </a>
                </div>
              </object>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <details className="text-xs text-slate-500" data-testid="pdf-unblock-help">
                  <summary className="cursor-pointer select-none hover:text-slate-700">Preview looks blank or blocked?</summary>
                  <div className="mt-2 max-w-xl space-y-1 rounded-md bg-slate-50 p-3 leading-relaxed">
                    <p>Chrome sometimes downloads PDFs instead of showing them inline. To view previews here:</p>
                    <p>1. Chrome menu (⋮) → <b>Settings</b> → <b>Privacy and security</b> → <b>Site settings</b>.</p>
                    <p>2. → <b>Additional content settings</b> → <b>PDF documents</b>.</p>
                    <p>3. Turn <b>OFF</b> “Download PDFs” so Chrome opens them in the browser.</p>
                    <p>4. If you use an ad blocker, allow this site, then reopen the preview.</p>
                    <p className="text-slate-400">You can always use “Open in new tab” or “Download PDF” below instead.</p>
                  </div>
                </details>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => window.open(previewUrl, "_blank", "noopener")}
                    data-testid="pdf-open-tab-btn"
                    className="gap-1.5"
                  >
                    <ExternalLink className="h-4 w-4" /> Open in new tab
                  </Button>
                  <a href={previewUrl} download={`quote-${detail?.reference}.pdf`} data-testid="pdf-download-btn">
                    <Button className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                      <FileText className="h-4 w-4" /> Download PDF
                    </Button>
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-64 items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-head">Email quote #{detail?.reference}</DialogTitle>
            <DialogDescription>
              The customer receives a secure link to the SKU quote PDF. Totals are re-validated before sending.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-500 text-slate-700">Recipient email</label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="customer@email.com"
              data-testid="recipient-input"
              className="focus-visible:ring-2 focus-visible:ring-blue-500"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={doSend} disabled={sending || !recipient} data-testid="confirm-send-btn" className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold, green }) {
  return (
    <div className="flex justify-between">
      <span className={green ? "text-green-600" : "text-slate-500"}>{label}</span>
      <span className={`font-mono ${bold ? "font-600 text-slate-900" : green ? "font-500 text-green-600" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

function ValidationPanel({ v, sym }) {
  if (!v) return null;
  const passed = v.passed;
  return (
    <div
      data-testid="validation-panel"
      className={`rounded-lg border p-5 ${passed ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}
    >
      <div className="mb-3 flex items-center gap-2">
        {passed ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : (
          <XCircle className="h-5 w-5 text-red-600" />
        )}
        <span className={`font-head text-sm font-600 ${passed ? "text-green-800" : "text-red-800"}`} data-testid="validation-status">
          {passed ? "Totals match Lightspeed exactly — safe to send" : "MISMATCH — sending is blocked"}
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {v.checks.map((c, i) => (
          <div key={i} className="rounded-md border border-white/60 bg-white/70 p-3" data-testid={`check-${i}`}>
            <div className="text-xs text-slate-500">{c.label}</div>
            <div className="mt-1 flex items-center justify-between font-mono text-sm">
              <span className="text-slate-900">{sym}{c.source.toFixed(2)}</span>
              {c.passed ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <span className="text-red-600">Δ {sym}{c.delta.toFixed(2)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {!passed && (
        <div className="mt-3 flex items-center gap-1.5 text-xs text-red-700">
          <AlertTriangle className="h-3.5 w-3.5" />
          Computed totals differ from Lightspeed. The quote may have been edited — re-open before sending.
        </div>
      )}
    </div>
  );
}

function EmptyDetail() {
  return (
    <div className="flex h-full min-h-[360px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white text-center">
      <FileText className="h-10 w-10 text-slate-300" />
      <p className="mt-3 font-head text-lg font-500 text-slate-700">Select a quote</p>
      <p className="mt-1 max-w-xs text-sm text-slate-400">
        Pick a quote from the list to reconstruct it by SKU, validate its totals, and email it.
      </p>
    </div>
  );
}
